import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * Guard a route behind authentication.
 *
 * While the auth context is still resolving, render a neutral placeholder
 * (so a flash of either the gated page OR the login page is avoided).
 *
 * When a signed-out visitor hits a protected route, forward them to
 * /login with the originally-requested pathname + search preserved as a
 * `?redirect=` query param AND in `state.from`. The sign-in / sign-up
 * pages both honour `?redirect=`, so a successful auth returns the user
 * to their intended destination (role-safe fallbacks still apply inside
 * Login.pickDestination).
 *
 * The redirect value is always an INTERNAL relative path. We strip any
 * scheme/host just in case a caller composed it incorrectly, so an open
 * redirect is not possible.
 */
export default function ProtectedRoute({ children, redirectTo = "/login" }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-alabaster" data-testid="protected-route-loading">
        <div className="animate-pulse">
          <div className="h-8 w-32 bg-heritage-navy/20 rounded"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Only forward safe internal paths (must begin with a single `/` and
    // not a protocol-relative `//`). Anything suspicious collapses to "/".
    const raw = `${location.pathname}${location.search || ""}`;
    const safe = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
    const target = `${redirectTo}?redirect=${encodeURIComponent(safe)}`;
    return <Navigate to={target} replace state={{ from: safe }} />;
  }

  return children;
}
